import { flattenObject } from "../../viewer-components/utils/property-object-flattener";

/** A feature-like object with just enough shape to sample real property types/values from. */
export interface SampleFeature {
  properties?: Record<string, unknown>;
}

/** One node of the property tree shown by the property-tree picker — either a branch (nested object) or a leaf. */
export interface IPropertyTreeNode {
  name: string;
  path: string[];
  isLeaf: boolean;
  children?: IPropertyTreeNode[];
}

/** Default cutoff for {@link distinctStringOptions} — above this cardinality, free text is offered instead of a dropdown. */
export const DEFAULT_MAX_CARDINALITY = 8;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function buildLevel(objects: Record<string, unknown>[], parentPath: string[]): IPropertyTreeNode[] {
  const keys = new Set<string>();
  objects.forEach((obj) => Object.keys(obj ?? {}).forEach((key) => keys.add(key)));

  return Array.from(keys)
    .sort()
    .map((key): IPropertyTreeNode => {
      const path = [...parentPath, key];
      const childObjects = objects.filter((obj) => isPlainObject(obj?.[key])).map((obj) => obj[key] as Record<string, unknown>);

      if (childObjects.length > 0) {
        return { name: key, path, isLeaf: false, children: buildLevel(childObjects, path) };
      }
      return { name: key, path, isLeaf: true };
    });
}

/**
 * Builds a tree of a layer's feature properties for the property-tree picker, unioning keys across
 * all sample features and recursing into nested plain objects. Arrays and primitives are always
 * leaves — array elements are not descended into (a materially bigger feature, out of scope here).
 */
export function buildPropertyTree(features: SampleFeature[]): IPropertyTreeNode[] {
  return buildLevel(
    features.map((f) => f?.properties ?? {}),
    [],
  );
}

/** Walks `path` via nested property access on every feature (reusing the shared flattenObject dot-path util), collecting all defined, non-null values found. */
export function sampleValuesForPath(features: SampleFeature[], path: string[]): unknown[] {
  const key = path.join(".");
  const values: unknown[] = [];

  for (const feature of features) {
    const value = flattenObject(feature?.properties ?? {})[key];
    if (value !== undefined && value !== null) values.push(value);
  }

  return values;
}

/** Infers the widget kind for a value-input from the first sampled value's real type — defaults to "string". */
export function detectValueKind(values: unknown[]): "boolean" | "number" | "string" {
  const first = values.find((value) => value !== undefined);
  if (typeof first === "boolean") return "boolean";
  if (typeof first === "number") return "number";
  return "string";
}

/**
 * Returns the sorted distinct string values across `values` when their cardinality is small enough
 * to offer as a dropdown, or `null` when there are none or too many (caller falls back to free text).
 */
export function distinctStringOptions(values: unknown[], maxCardinality = DEFAULT_MAX_CARDINALITY): string[] | null {
  const distinct = new Set(values.map((value) => String(value)));
  if (distinct.size === 0 || distinct.size > maxCardinality) return null;
  return Array.from(distinct).sort();
}
